import { useRef, useEffect } from 'react';

const reasons = [
  {
    number: '01',
    title: 'Traditional Methods',
    description: 'We use stone grinding, hand-pressing and age-old techniques that preserve the full nutritional profile of every ingredient — exactly as nature intended.',
  },
  {
    number: '02',
    title: 'Zero Chemicals',
    description: 'No preservatives, no artificial colours, no additives of any kind. Pearl Foods products are as close to nature as food can possibly be.',
  },
  {
    number: '03',
    title: 'Small Batch Handmade',
    description: 'Every batch is made in small quantities, allowing us to maintain the highest standards of quality, freshness and care in every product.',
  },
  {
    number: '04',
    title: 'Trusted Natural Remedies',
    description: 'Our product formulations are rooted in traditional herbal wisdom, trusted by communities for generations for their genuine health benefits.',
  },
];

const WhyChoose = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.scroll-animate').forEach((child) => {
              child.classList.add('visible');
            });
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-brand-beige px-4 md:px-8" ref={ref}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 scroll-animate">
          <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium mb-3">
            Why Pearl Foods
          </span>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-brown-dark font-bold mb-4">
            The Pearl Foods Difference
          </h2>
          <div className="w-16 h-0.5 bg-brand-gold mx-auto"></div>
        </div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10">
          {reasons.map((reason, index) => (
            <div
              key={reason.number}
              className={`scroll-animate scroll-animate-delay-${(index % 2) + 1} relative`}
            >
              <div className="flex items-start gap-5">
                <span className="font-serif text-5xl md:text-6xl font-bold text-brand-olive/20 leading-none flex-shrink-0 select-none">
                  {reason.number}
                </span>
                <div className="pt-3">
                  <h3 className="font-serif text-xl text-brand-brown-dark font-semibold mb-3">
                    {reason.title}
                  </h3>
                  <p className="font-sans text-sm text-brand-brown/70 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </div>
              {index < reasons.length - 1 && (
                <div className="absolute bottom-0 left-0 right-0 h-px bg-brand-beige-light mt-6 hidden sm:block"></div>
              )}
            </div>
          ))}
        </div>

        {/* Trust Bar */}
        <div className="mt-16 scroll-animate flex flex-wrap justify-center gap-8 md:gap-14 border-t border-brand-beige-light pt-12">
          {[
            { icon: 'ri-award-line', label: 'Premium Quality', value: '100%' },
            { icon: 'ri-seedling-line', label: 'Organic Sourced', value: 'Always' },
            { icon: 'ri-hand-heart-line', label: 'Handmade Batches', value: 'Small' },
            { icon: 'ri-map-pin-2-line', label: 'Based in Islamabad', value: 'Local' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2 bg-brand-olive/10 rounded-full">
                <i className={`${stat.icon} text-xl text-brand-olive`}></i>
              </div>
              <p className="font-serif text-2xl text-brand-brown-dark font-bold">{stat.value}</p>
              <p className="font-sans text-xs text-brand-brown/60 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
